<template>
  <div class="view-container">
    <!-- Header -->
    <div class="view-header" style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;">
      <div>
        <h1 class="view-title">告警中心</h1>
        <p class="view-subtitle">系統健康狀態綜合監控</p>
      </div>
      <div style="display:flex;align-items:center;gap:10px;">
        <span v-if="lastRefreshed" class="text-sm" style="color:var(--gray-500);">
          上次更新：{{ lastRefreshed }}
        </span>
        <button class="btn btn-secondary btn-sm" :disabled="alertsLoading" @click="refresh">
          <svg v-if="alertsLoading" class="spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.1-9.05"/></svg>
          重新整理
        </button>
      </div>
    </div>

    <!-- Summary Cards -->
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:14px;margin-bottom:24px;">
      <div class="card p-4" style="text-align:center;">
        <div style="font-size:2rem;font-weight:700;color:var(--color-danger);">{{ alertSummary.danger }}</div>
        <div class="text-sm" style="color:var(--gray-500);margin-top:4px;">嚴重告警</div>
      </div>
      <div class="card p-4" style="text-align:center;">
        <div style="font-size:2rem;font-weight:700;color:var(--color-warning);">{{ alertSummary.warning }}</div>
        <div class="text-sm" style="color:var(--gray-500);margin-top:4px;">警告</div>
      </div>
      <div class="card p-4" style="text-align:center;">
        <div style="font-size:2rem;font-weight:700;color:var(--color-success);">{{ alertSummary.info }}</div>
        <div class="text-sm" style="color:var(--gray-500);margin-top:4px;">正常</div>
      </div>
      <div class="card p-4" style="text-align:center;">
        <div style="font-size:2rem;font-weight:700;color:var(--gray-700);">{{ alertSummary.total }}</div>
        <div class="text-sm" style="color:var(--gray-500);margin-top:4px;">監控項目</div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="alertsLoading && !alertCenterAlerts.length" class="card p-8" style="text-align:center;color:var(--gray-500);">
      <div class="spin" style="width:28px;height:28px;border:3px solid var(--border);border-top-color:var(--brand-500);border-radius:50%;margin:0 auto 12px;"></div>
      載入中…
    </div>

    <!-- Alert Groups -->
    <template v-else>
      <!-- Danger Alerts -->
      <div v-if="dangerAlerts.length" class="card mb-4" style="border-left:4px solid var(--color-danger);">
        <div class="card-header" style="display:flex;align-items:center;gap:8px;padding:14px 16px;border-bottom:1px solid var(--border);">
          <span style="width:10px;height:10px;border-radius:50%;background:var(--color-danger);display:inline-block;flex-shrink:0;"></span>
          <span style="font-weight:600;color:var(--color-danger);">嚴重告警</span>
          <span class="badge badge-danger">{{ dangerAlerts.length }}</span>
        </div>
        <div>
          <div
            v-for="alert in dangerAlerts"
            :key="alert.id"
            class="alert-row"
            style="display:flex;align-items:flex-start;gap:12px;padding:12px 16px;border-bottom:1px solid var(--border);"
          >
            <span class="alert-cat-icon" :title="categoryLabel(alert.category)">{{ categoryIcon(alert.category) }}</span>
            <div style="flex:1;min-width:0;">
              <div style="font-weight:500;color:var(--gray-900);">{{ alert.title }}</div>
              <div v-if="alert.detail" class="text-sm" style="color:var(--gray-500);margin-top:2px;">{{ alert.detail }}</div>
            </div>
            <span class="badge badge-default text-xs" style="flex-shrink:0;">{{ categoryLabel(alert.category) }}</span>
          </div>
        </div>
      </div>

      <!-- Warning Alerts -->
      <div v-if="warningAlerts.length" class="card mb-4" style="border-left:4px solid var(--color-warning);">
        <div class="card-header" style="display:flex;align-items:center;gap:8px;padding:14px 16px;border-bottom:1px solid var(--border);">
          <span style="width:10px;height:10px;border-radius:50%;background:var(--color-warning);display:inline-block;flex-shrink:0;"></span>
          <span style="font-weight:600;color:var(--color-warning);">警告</span>
          <span class="badge badge-warning">{{ warningAlerts.length }}</span>
        </div>
        <div>
          <div
            v-for="alert in warningAlerts"
            :key="alert.id"
            class="alert-row"
            style="display:flex;align-items:flex-start;gap:12px;padding:12px 16px;border-bottom:1px solid var(--border);"
          >
            <span class="alert-cat-icon" :title="categoryLabel(alert.category)">{{ categoryIcon(alert.category) }}</span>
            <div style="flex:1;min-width:0;">
              <div style="font-weight:500;color:var(--gray-900);">{{ alert.title }}</div>
              <div v-if="alert.detail" class="text-sm" style="color:var(--gray-500);margin-top:2px;">{{ alert.detail }}</div>
            </div>
            <span class="badge badge-default text-xs" style="flex-shrink:0;">{{ categoryLabel(alert.category) }}</span>
          </div>
        </div>
      </div>

      <!-- Info / OK Items -->
      <div v-if="infoAlerts.length" class="card mb-4" style="border-left:4px solid var(--color-success);">
        <div
          class="card-header"
          style="display:flex;align-items:center;gap:8px;padding:14px 16px;border-bottom:1px solid var(--border);cursor:pointer;"
          @click="showInfo = !showInfo"
        >
          <span style="width:10px;height:10px;border-radius:50%;background:var(--color-success);display:inline-block;flex-shrink:0;"></span>
          <span style="font-weight:600;color:var(--color-success);">正常項目</span>
          <span class="badge badge-success">{{ infoAlerts.length }}</span>
          <svg
            :style="{ transform: showInfo ? 'rotate(180deg)' : 'rotate(0deg)', transition:'transform 0.2s', marginLeft:'auto' }"
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          ><polyline points="6 9 12 15 18 9"/></svg>
        </div>
        <div v-if="showInfo">
          <div
            v-for="alert in infoAlerts"
            :key="alert.id"
            class="alert-row"
            style="display:flex;align-items:flex-start;gap:12px;padding:12px 16px;border-bottom:1px solid var(--border);"
          >
            <span class="alert-cat-icon" :title="categoryLabel(alert.category)">{{ categoryIcon(alert.category) }}</span>
            <div style="flex:1;min-width:0;">
              <div style="font-weight:500;color:var(--gray-700);">{{ alert.title }}</div>
              <div v-if="alert.detail" class="text-sm" style="color:var(--gray-500);margin-top:2px;">{{ alert.detail }}</div>
            </div>
            <span class="badge badge-default text-xs" style="flex-shrink:0;">{{ categoryLabel(alert.category) }}</span>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!alertCenterAlerts.length && !alertsLoading" class="card p-8" style="text-align:center;color:var(--gray-400);">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin:0 auto 12px;display:block;"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        <p>目前無任何告警資料，請點擊「重新整理」載入</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAppStore } from '../store/app'
import { storeToRefs } from 'pinia'

const store = useAppStore()
const { alertCenterAlerts, alertSummary, alertsLoading } = storeToRefs(store)
const { fetchAlerts } = store

const showInfo = ref(false)
const lastRefreshed = ref('')

const dangerAlerts  = computed(() => alertCenterAlerts.value.filter(a => a.severity === 'danger'))
const warningAlerts = computed(() => alertCenterAlerts.value.filter(a => a.severity === 'warning'))
const infoAlerts    = computed(() => alertCenterAlerts.value.filter(a => a.severity === 'info'))

const CAT_LABELS = {
  disk: '磁碟',
  services: '服務',
  mail_queue: '郵件佇列',
  ssl: 'SSL 憑證',
  backup: '備份',
  clamav: '病毒防護',
}
const CAT_ICONS = {
  disk: '💾',
  services: '⚙️',
  mail_queue: '📨',
  ssl: '🔒',
  backup: '📦',
  clamav: '🛡️',
}
function categoryLabel(cat) { return CAT_LABELS[cat] || cat }
function categoryIcon(cat)  { return CAT_ICONS[cat] || '📋' }

async function refresh() {
  await fetchAlerts()
  const now = new Date()
  lastRefreshed.value = now.toLocaleTimeString('zh-TW', { hour:'2-digit', minute:'2-digit', second:'2-digit' })
}

onMounted(() => { refresh() })
</script>

<style scoped>
.spin {
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.alert-cat-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
  margin-top: 1px;
}
.alert-row:last-child {
  border-bottom: none !important;
}
</style>
